import React from 'react';
import {
  DNACollapsibleCard,
  DNABox,
  DNAButton,
  DNAText,
  Iffy,
} from '@alucio/lux-ui';
import { AssociatedFileORM, DocumentORM, EntityWithFiles } from 'src/types/types'
import AssociatedFilesList, {
  Mode,
} from 'src/components/DNA/AssociatedFiles/AssociatedFilesList'
import FileUpload, { ManagedFile, UploadStatus } from 'src/components/DNA/FileUpload/FileUpload'
import AssociatedFilesSearchInput from 'src/screens/Publishers/AssociatedFilesSearchInput'

// AMPLIFY
import {
  AssociatedFile,
  AttachedFile,
  FileType,
} from '@alucio/aws-beacon-amplify/src/models';

const ASSOCIATED_FILES_DESCRIPTION = 'Associated files are files related to the master version that can \n' +
  'be designated for the field team to share externally.';

interface AssociatedFilesManagerProps<T> {
  entityORM: T,
  isDisabled: boolean;
  searching: boolean;
  setSearching: (value: React.SetStateAction<boolean>) => void;
  associatedFilesMode: Mode | keyof typeof Mode,
  files: (AssociatedFileORM | ManagedFile)[],
  onUploadStatusChange: (status: UploadStatus) => void,
  onSelect: (parentDoc: T, linkedDoc: DocumentORM) => void
  onDelete: (entityORM: T, item: DocumentORM | AttachedFile) => void,
  title?: string;
  canLinkFiles?: boolean;
  canUploadFiles?: boolean;
  emptyMessage?: string;
  showTooltip?: boolean;
  onFinishedUpload?: (attachedFile: AttachedFile) => void,
  onUpdateAssociatedFile?: (associatedFile: AssociatedFile) => void,
}

const AssociatedFilesManager = <T extends EntityWithFiles>(props: AssociatedFilesManagerProps<T>) => {
  const {
    entityORM,
    files,
    canLinkFiles = true,
    canUploadFiles = canLinkFiles,
    isDisabled,
    searching,
    setSearching,
    emptyMessage,
    onUploadStatusChange,
    associatedFilesMode,
    onSelect,
    onDelete,
    onFinishedUpload,
    title = 'Associated Files',
    onUpdateAssociatedFile,
    showTooltip = true,
  } = props;

  return (
    <FileUpload>
      {
        (fileUpload) => (
          <>
            <DNACollapsibleCard
              headerTitle={title}
              collapsedDefault={!files.length}
              popoverInfoText={showTooltip ? ASSOCIATED_FILES_DESCRIPTION : undefined}
              customWrapperStyle={{
                paddingVertical: 26,
              }}
            >
              <Iffy is={canLinkFiles || canUploadFiles}>
                <DNABox spacing="small">
                  <Iffy is={canLinkFiles}>
                    <DNABox spacing="small" alignY="center">
                      <DNAButton
                        iconLeft="link-variant"
                        appearance="outline"
                        status="subtle"
                        size="small"
                        disabled={isDisabled}
                        onPress={() => setSearching(true)}
                      >
                        <DNAText>Link files</DNAText>
                      </DNAButton>
                    </DNABox>
                  </Iffy>
                  <Iffy is={canUploadFiles}>
                    <DNABox spacing="small" alignY="center">
                      <FileUpload.Input
                        s3PathPrefix="publish_tmp/"
                        fileExt={Object.keys(FileType)}
                        multiFile
                        processOnSelect
                        disabled={isDisabled}
                      >
                        <DNAButton
                          iconLeft="upload"
                          appearance="outline"
                          status="subtle"
                          disabled={isDisabled}
                          size="small"
                        >
                          <DNAText>Upload</DNAText>
                        </DNAButton>
                      </FileUpload.Input>
                    </DNABox>
                  </Iffy>
                </DNABox>
              </Iffy>
              { /* SEARCH FILE INPUT */ }
              <Iffy is={searching}>
                <DNABox fill spacing="medium" childFill={0} alignY="center">
                  <AssociatedFilesSearchInput parentDoc={entityORM} onSelect={onSelect} />
                  <DNAButton
                    appearance="outline"
                    status="subtle"
                    context="round"
                    size="small"
                    style={{ marginBottom: 5 }}
                    onPress={() => setSearching(false)}
                  >
                    <DNAText>Cancel</DNAText>
                  </DNAButton>
                </DNABox>
              </Iffy>
              {/* NORMAL ASSOCIATED FILES LIST */}
              <AssociatedFilesList
                emptyMessage={emptyMessage}
                entityORM={entityORM}
                onStatusChange={onUploadStatusChange}
                mode={associatedFilesMode}
                onDelete={onDelete}
                onFinishedUpload={onFinishedUpload}
                items={[...files, ...(fileUpload?.files ?? [])]}
                onUpdateAssociatedFile={onUpdateAssociatedFile}
              />
            </DNACollapsibleCard>
          </>
        )
      }
    </FileUpload>
  )
}

AssociatedFilesManager.displayeName = 'AssociatedFilesManager';

export default AssociatedFilesManager;
